import { useEffect } from "react";
import { getProspectReference } from "@utils/storage";
import { useViewAvailableAppointments as useViewAvailableAppointmentsSWR } from "@modules/eirSalesFacade/swr/viewAvailableAppointments";
import { useFetchState } from "@hooks/useFetchState";
import { IEirSalesViewAvailableAppointments } from "@modules/eirSalesFacade/interfaces/models/IEirSalesViewAvailableAppointments";
import { EEirSalesViewAvailableAppointmentsStatus } from "@modules/eirSalesFacade/constants";

const POLLING_INTERVAL_MS = 3000;

interface IParams {
    offerId: number;
    shouldPoll?: boolean;
    isEnabled?: boolean;
}

export const useViewAvailableAppointments = (props: IParams) => {
    const { offerId, shouldPoll = false, isEnabled = true } = props;
    const prospectReference = getProspectReference();
    const { startFetching, endFetching, error: fetchError, isLoading: isFetchLoading } = useFetchState();
    const canGetAvailableAppointments = Boolean(isEnabled && shouldPoll && prospectReference);

    const { data, error, isLoading, mutate } = useViewAvailableAppointmentsSWR(
        prospectReference,
        offerId,
        canGetAvailableAppointments,
        {
            refreshInterval: (appointments: IEirSalesViewAvailableAppointments | undefined): number => {
                if (
                    !appointments?.availableSlots ||
                    appointments.status === EEirSalesViewAvailableAppointmentsStatus.PENDING
                ) {
                    return POLLING_INTERVAL_MS;
                }

                return 0;
            },
        }
    );

    const isPending = data?.status === EEirSalesViewAvailableAppointmentsStatus.PENDING || !data;
    const isSuccess =
        data?.status === EEirSalesViewAvailableAppointmentsStatus.PENDING_TIMEOUT ||
        data?.status === EEirSalesViewAvailableAppointmentsStatus.SUCCESS;
    const isStatusLoading = (isLoading || isPending) && !isSuccess && !error;

    useEffect(() => {
        if (isStatusLoading) {
            startFetching();
        } else {
            endFetching(error);
        }
    }, [isStatusLoading, error, startFetching, endFetching]);

    return {
        availableAppointments: data,
        error: error ?? fetchError,
        isLoading: isSuccess ? false : isStatusLoading || isFetchLoading,
        mutate,
    };
};
